/**
 * Transport facade for screens.
 *
 * Re-exports invoke/onPush/IpcError so existing screen code (`import { invoke }
 * from '../lib/ipc'`) keeps working unchanged. Behind the scenes, both functions
 * delegate to the DataSource registered by the shell at startup.
 *
 * In Electron the shell installs LocalDataSource (window.cipherops bridge).
 * In the web shell the shell installs RemoteDataSource (fetch + WebSocket).
 *
 * If no DataSource is registered (e.g. running in Storybook with no shell),
 * the call fails fast with a clear error rather than silently no-oping.
 *
 * --- Request-ID propagation ---
 * Every web-shell `invoke()` ends up sending an `X-Request-Id` header
 * (UUIDv4, fresh per call) to the API server, which echoes it back. On any
 * failure the id is attached to the thrown `IpcError` (`err.requestId`) so
 * UI surfaces — error toasts, bug-report forms, log lines — can quote it.
 *
 * The header round-trip lives in RemoteDataSource (`packages/data-source/
 * src/remote.ts`); this file is the canonical screen-side entry point and
 * a convenient place to read the helper if you need it.
 *
 * See apps/server/REQUEST-ID.md for the full story.
 */

import { getDataSource, IpcError } from '@cipherops/data-source';

export { IpcError };

export async function invoke<T = unknown>(
  channel: string,
  payload: unknown = {},
): Promise<T> {
  return getDataSource().invoke<T>(channel, payload);
}

export function onPush(event: string, handler: (payload: unknown) => void): () => void {
  return getDataSource().onPush(event, handler);
}

/**
 * Read the request id off a thrown error (or any value), if any.
 *
 * Helper for error toasts / bug-report forms. Returns null when the value
 * isn't an IpcError or doesn't carry a requestId (e.g. local-IPC errors,
 * synchronous validation failures). Safe to call on `unknown`.
 *
 *   try {
 *     await invoke('cipherops/findings/create', payload);
 *   } catch (err) {
 *     const rid = getRequestId(err);
 *     toast.error(`Failed to save${rid ? ` (id: ${rid})` : ''}`);
 *     throw err;
 *   }
 */
export function getRequestId(err: unknown): string | null {
  if (err instanceof IpcError && typeof err.requestId === 'string' && err.requestId.length > 0) {
    return err.requestId;
  }
  // Defensive — duck-typed for cases where the IpcError class identity is
  // mismatched across bundle boundaries (workspace symlinks vs hoisted copies).
  if (
    err &&
    typeof err === 'object' &&
    'requestId' in err &&
    typeof (err as { requestId: unknown }).requestId === 'string' &&
    (err as { requestId: string }).requestId.length > 0
  ) {
    return (err as { requestId: string }).requestId;
  }
  return null;
}
